import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Lightning, ArrowClockwise, MagnifyingGlass, CurrencyInr,
  Receipt, Storefront, ArrowLeft, ArrowRight, Circle, Export,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LiveDataPage() {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [mobileFilter, setMobileFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  const fetchData = useCallback(async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", 25);
      if (search) params.append("mobile_no", search);
      if (brandFilter && brandFilter !== "all") params.append("brand_name", brandFilter);
      if (mobileFilter && mobileFilter !== "all") params.append("has_mobile", mobileFilter);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      const [txnRes, statsRes] = await Promise.all([
        axios.get(`${API}/transactions?${params.toString()}`),
        axios.get(`${API}/transactions/stats`),
      ]);

      setTransactions(txnRes.data.transactions);
      setTotal(txnRes.data.total);
      setPages(txnRes.data.pages);
      setStats(statsRes.data);
      setLastUpdated(new Date());
    } catch (error) {
      if (!silent) toast.error("Failed to load transaction data");
    } finally {
      setIsLoading(false);
    }
  }, [page, search, brandFilter, mobileFilter, startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => fetchData(true), 10000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, fetchData]);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); fetchData(); };

  const formatAmount = (amt) => {
    if (!amt && amt !== 0) return "-";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amt);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr.substring(0, 19);
      return format(d, "dd MMM yyyy, HH:mm");
    } catch {
      return dateStr.substring(0, 19);
    }
  };

  const exportCSV = () => {
    const params = new URLSearchParams();
    if (search) params.append("mobile_no", search);
    if (brandFilter && brandFilter !== "all") params.append("brand_name", brandFilter);
    // Build CSV from current data
    const headers = ["Transaction ID","Mall ID","Brand","Bill No","Bill Date","Amount","Type","Mobile","Customer"];
    const rows = transactions.map(t => [
      t.transaction_id, t.mall_id, t.brand_name, t.bill_no,
      t.bill_date, t.bill_amount, t.bill_type || "", t.mobile_no || "", t.customer_name || ""
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.map(c => `"${c}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transactions_${format(new Date(), "yyyyMMdd_HHmm")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const brandOptions = stats?.by_brand?.map(b => b.brand).filter(Boolean) || [];

  return (
    <div className="space-y-6" data-testid="live-data-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#0A0A0A] flex items-center gap-2">
            <Lightning size={28} className="text-[#002FA7]" weight="fill" />
            Live API Data
          </h2>
          <p className="text-[#64748B] text-sm mt-1 flex items-center gap-2">
            Real-time transaction data from ADSR / Fundle
            {autoRefresh && (
              <span className="inline-flex items-center gap-1 text-[#10B981] text-xs">
                <Circle size={8} weight="fill" className="animate-pulse" />LIVE
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? "bg-[#10B981] hover:bg-[#059669] gap-1" : "gap-1"}
            data-testid="auto-refresh-toggle"
          >
            <Circle size={10} weight="fill" />
            {autoRefresh ? "Auto-Refresh ON" : "Auto-Refresh OFF"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchData()} className="gap-1" data-testid="manual-refresh-btn">
            <ArrowClockwise size={16} />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV} className="gap-1" data-testid="export-csv-btn">
            <Export size={16} />CSV
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border border-[#E2E8F0]" data-testid="stat-total-txn">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-[#DBEAFE] flex items-center justify-center flex-shrink-0">
                <Receipt size={22} className="text-[#002FA7]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-[#64748B]">Total Transactions</p>
                <p className="font-heading text-2xl font-bold">{stats.total_transactions?.toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-[#E2E8F0]" data-testid="stat-total-amount">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-[#DCFCE7] flex items-center justify-center flex-shrink-0">
                <CurrencyInr size={22} className="text-[#166534]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-[#64748B]">Total Amount</p>
                <p className="font-heading text-2xl font-bold">{formatAmount(stats.total_amount)}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-[#E2E8F0] col-span-2" data-testid="stat-brand-breakdown">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-[0.15em] text-[#64748B] mb-2">Top Brands by Revenue</p>
              <div className="flex flex-wrap gap-2">
                {stats.by_brand?.slice(0, 6).map((b, i) => (
                  <Badge key={i} className="bg-[#F8FAFC] text-[#0A0A0A] border border-[#E2E8F0] py-1 px-2 text-xs font-normal">
                    <Storefront size={12} className="mr-1 text-[#002FA7]" />
                    {b.brand}: {formatAmount(b.amount)} ({b.count})
                  </Badge>
                ))}
                {(!stats.by_brand || stats.by_brand.length === 0) && (
                  <span className="text-sm text-[#94A3B8]">No brand data yet</span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border border-[#E2E8F0]">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                  <Input
                    placeholder="Search by mobile number..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="txn-search-input"
                  />
                </div>
                <Button type="submit" variant="secondary" data-testid="txn-search-btn">Search</Button>
              </form>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Select value={brandFilter} onValueChange={(v) => { setBrandFilter(v); setPage(1); }}>
                <SelectTrigger className="w-48" data-testid="txn-brand-filter">
                  <SelectValue placeholder="All Brands" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  {brandOptions.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={mobileFilter} onValueChange={(v) => { setMobileFilter(v); setPage(1); }}>
                <SelectTrigger className="w-48" data-testid="txn-mobile-filter">
                  <SelectValue placeholder="Mobile: All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Records</SelectItem>
                  <SelectItem value="yes">With Mobile</SelectItem>
                  <SelectItem value="no">Without Mobile</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-2 items-center">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                  className="w-40"
                  data-testid="txn-start-date"
                />
                <span className="text-[#64748B] text-sm">to</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                  className="w-40"
                  data-testid="txn-end-date"
                />
              </div>
              {(brandFilter || mobileFilter || startDate || endDate || search) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setBrandFilter(""); setMobileFilter(""); setStartDate(""); setEndDate(""); setSearch(""); setPage(1); }}
                  className="text-red-500 hover:text-red-700"
                  data-testid="txn-clear-filters"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card className="border border-[#E2E8F0]">
        <CardHeader className="border-b border-[#E2E8F0] pb-4">
          <CardTitle className="font-heading text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Lightning size={22} className="text-[#002FA7]" />
              Transaction Feed
            </span>
            <span className="text-sm font-normal text-[#64748B] flex items-center gap-3">
              {lastUpdated && (
                <span className="text-xs">Updated: {format(lastUpdated, "HH:mm:ss")}</span>
              )}
              {total.toLocaleString()} records
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-[#64748B]">
              <ArrowClockwise size={24} className="animate-spin mx-auto mb-2" />Loading...
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-12 text-center text-[#64748B]">
              <Receipt size={40} className="mx-auto mb-3 text-[#CBD5E1]" />
              <p className="font-medium">No transactions yet</p>
              <p className="text-sm mt-1">Transaction data will appear here when pushed via the ADSR API</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table className="data-table">
                  <TableHeader>
                    <TableRow className="bg-[#F8FAFC]">
                      <TableHead className="w-[50px]">#</TableHead>
                      <TableHead>Transaction ID</TableHead>
                      <TableHead>Mall</TableHead>
                      <TableHead>Brand</TableHead>
                      <TableHead>Bill No</TableHead>
                      <TableHead>Bill Date</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Received At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((txn, idx) => (
                      <TableRow key={txn.id} className="table-row-hover" data-testid={`txn-row-${txn.id}`}>
                        <TableCell className="text-xs text-[#94A3B8] font-mono">{(page - 1) * 25 + idx + 1}</TableCell>
                        <TableCell className="font-mono text-xs max-w-[140px] truncate" title={txn.transaction_id}>
                          {txn.transaction_id}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-[#EFF6FF] text-[#1E40AF] text-xs">{txn.mall_id}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-[#FEF3C7] text-[#92400E] text-xs">{txn.brand_name}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{txn.bill_no}</TableCell>
                        <TableCell className="text-xs text-[#64748B]">{formatDate(txn.bill_date)}</TableCell>
                        <TableCell className="text-right font-mono text-sm font-medium">{formatAmount(txn.bill_amount)}</TableCell>
                        <TableCell>
                          {txn.bill_type ? (
                            <Badge className={txn.bill_type === "SALE" ? "bg-[#DCFCE7] text-[#166534] text-xs" : "bg-[#FEF2F2] text-[#991B1B] text-xs"}>
                              {txn.bill_type}
                            </Badge>
                          ) : <span className="text-[#94A3B8]">-</span>}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{txn.mobile_no || <span className="text-[#94A3B8]">-</span>}</TableCell>
                        <TableCell className="text-sm max-w-[120px] truncate">{txn.customer_name || <span className="text-[#94A3B8]">-</span>}</TableCell>
                        <TableCell className="text-xs text-[#64748B]">{formatDate(txn.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E8F0]">
                <p className="text-sm text-[#64748B]">
                  Page {page} of {pages} ({total.toLocaleString()} total)
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} data-testid="txn-prev-page">
                    <ArrowLeft size={16} />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} data-testid="txn-next-page">
                    <ArrowRight size={16} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
